import type { FinTaxCalendarSummary, FinTaxCalendarItemRow } from '@shared/api';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { Table } from '@shared/ui/Table';
import { IconPlus } from '@shared/ui/icons';
import {
  formatDateRu,
  formatMillions,
  formatMoney,
  TAX_TYPE_LABELS,
} from '../financierFormat';
import {
  Section,
  SectionHeader,
  SectionTitle,
  Subtitle,
  StatGrid,
  MetricBox,
  MetricLabel,
  MetricValue,
  MetricNote,
  TableWrap,
  RightTh,
  RightTd,
  TableNoticeTd,
  PeriodText,
  DueWarningText,
  BoldText,
  DimSubText,
  ReservedPurpleTd,
  PaidGreenTd,
  HeaderActions,
  QuarterPicker,
  Select,
  KpiNotice,
} from './styled';
import { TAX_STATUS_LABELS } from './types';
import {
  calendarReserved,
  coverageText,
  quarterFromKey,
  quarterKey,
  quarterLabel,
  vatDifference,
  type Quarter,
  type QuarterKpiState,
} from './taxPeriods';

interface TaxBalanceSectionProps {
  /** Показатели выбранного квартала: загрузка, ошибка или ответ именно по нему. */
  kpi: QuarterKpiState<FinTaxCalendarSummary>;
  /** Строки календаря из последнего ответа: от квартала они не зависят. */
  items: FinTaxCalendarItemRow[] | undefined;
  isItemsLoading: boolean;
  isItemsError: boolean;
  refetchTax: () => void;
  onOpenTaxModal: () => void;
  canWrite: boolean;
  /** Квартал оценки НДС: «2026-Q4». */
  vatQuarter: string;
  quarterOptions: Quarter[];
  onChangeQuarter: (key: string) => void;
}

export function TaxBalanceSection({
  kpi,
  items,
  isItemsLoading,
  isItemsError,
  refetchTax,
  onOpenTaxModal,
  canWrite,
  vatQuarter,
  quarterOptions,
  onChangeQuarter,
}: TaxBalanceSectionProps) {
  const taxData = kpi.kind === 'ready' ? kpi.data : undefined;
  const difference = taxData
    ? vatDifference(taxData.vat_outgoing_signed, taxData.vat_incoming_deductible)
    : 0;
  const skipped = taxData?.vat_outgoing_skipped_count ?? 0;
  const selectedQuarter = quarterFromKey(vatQuarter);
  const selectedLabel = selectedQuarter ? quarterLabel(selectedQuarter) : vatQuarter;

  return (
    <Section>
      <SectionHeader>
        <div>
          <SectionTitle>Оценка НДС за квартал и налоговый резерв</SectionTitle>
          <Subtitle>
            По всем компаниям. Исходящий НДС — по подписанным КС-3, входящий — по одобренным
            заявкам на оплату. Это оценка, а не данные учёта: сумма к уплате — по декларации.
          </Subtitle>
        </div>
        <HeaderActions>
          <QuarterPicker>
            Квартал
            <Select value={vatQuarter} onChange={(e) => onChangeQuarter(e.target.value)}>
              {quarterOptions.map((q) => (
                <option key={quarterKey(q)} value={quarterKey(q)}>
                  {quarterLabel(q)}
                </option>
              ))}
            </Select>
          </QuarterPicker>
          {canWrite && (
            <Button $variant="primary" $size="sm" onClick={onOpenTaxModal}>
              <IconPlus size={14} />
              Добавить налоговое обязательство
            </Button>
          )}
        </HeaderActions>
      </SectionHeader>

      {/* Пока выбранный квартал считается или если запрос не удался — не цифры прошлого */}
      {kpi.kind === 'loading' && (
        <KpiNotice role="status">Считаем оценку НДС за {selectedLabel}…</KpiNotice>
      )}
      {kpi.kind === 'error' && (
        <KpiNotice role="alert">
          Не удалось рассчитать оценку НДС за {selectedLabel}.
          <Button $variant="secondary" $size="sm" onClick={refetchTax}>
            Повторить
          </Button>
        </KpiNotice>
      )}
      {taxData && (
        <StatGrid>
          <MetricBox>
            <MetricLabel>
              Оценка исходящего НДС по подписанным КС-3 за {taxData.vat_quarter_label}
            </MetricLabel>
            <MetricValue>{formatMillions(taxData.vat_outgoing_signed)}</MetricValue>
            <MetricNote>
              {skipped > 0
                ? `Не вошли подписанные КС-3 без даты подписания или без ставки НДС на эту дату: ${skipped}`
                : 'Ставка НДС — на дату подписания КС-3'}
            </MetricNote>
          </MetricBox>

          <MetricBox>
            <MetricLabel>Оценка входящего НДС по одобренным заявкам</MetricLabel>
            <MetricValue $color="#10b981">{formatMillions(taxData.vat_incoming_deductible)}</MetricValue>
            <MetricNote>
              Одобренные, выгруженные в 1С и исполненные заявки с датой в {taxData.vat_quarter_label}
            </MetricNote>
          </MetricBox>

          <MetricBox>
            <MetricLabel>Разница (оценка)</MetricLabel>
            <MetricValue $color={difference > 0 ? '#ef4444' : '#10b981'}>
              {formatMillions(difference)}
            </MetricValue>
            <MetricNote>
              {difference < 0 ? 'Входящий НДС больше исходящего' : 'Исходящий минус входящий'}
            </MetricNote>
          </MetricBox>

          <MetricBox>
            <MetricLabel>Остаток на счёте налогового резерва</MetricLabel>
            <MetricValue $color="#8b5cf6">{formatMillions(taxData.tax_reserve_balance)}</MetricValue>
            <MetricNote>
              Покрытие разницы: <b>{coverageText(taxData.reserve_coverage_pct)}</b>
            </MetricNote>
          </MetricBox>

          <MetricBox>
            <MetricLabel>Зарезервировано по календарю налогов</MetricLabel>
            <MetricValue>{formatMillions(calendarReserved(taxData.items))}</MetricValue>
            <MetricNote>Неуплаченные обязательства в таблице ниже</MetricNote>
          </MetricBox>
        </StatGrid>
      )}

      <TableWrap>
        <Table>
          <thead>
            <tr>
              <th>Период / Срок уплаты</th>
              <th>Вид налога</th>
              <th>Обязательство / Юрлицо</th>
              <RightTh>Начислено, ₽</RightTh>
              <RightTh>Зарезервировано, ₽</RightTh>
              <RightTh>Уплачено, ₽</RightTh>
              <th>Статус ЕНС</th>
            </tr>
          </thead>
          <tbody>
            {isItemsLoading ? (
              <tr>
                <TableNoticeTd colSpan={7}>
                  Загрузка налогового календаря…
                </TableNoticeTd>
              </tr>
            ) : isItemsError ? (
              <tr>
                <TableNoticeTd colSpan={7}>
                  Не удалось загрузить налоговый календарь ЕНС.{' '}
                  <Button $variant="secondary" $size="sm" onClick={refetchTax}>
                    Повторить
                  </Button>
                </TableNoticeTd>
              </tr>
            ) : !items || items.length === 0 ? (
              <tr>
                <TableNoticeTd colSpan={7}>
                  Обязательства не найдены
                </TableNoticeTd>
              </tr>
            ) : (
              items.map((t: FinTaxCalendarItemRow) => {
                const conf = TAX_STATUS_LABELS[t.status] ?? {
                  label: t.status,
                  tone: 'neutral',
                };
                return (
                  <tr key={t.id}>
                    <td>
                      <PeriodText>{t.tax_period}</PeriodText>
                      <DueWarningText>
                        Срок: {formatDateRu(t.due_date)}
                      </DueWarningText>
                    </td>
                    <td>
                      <BoldText>
                        {TAX_TYPE_LABELS[t.tax_type] ?? t.tax_type}
                      </BoldText>
                    </td>
                    <td>
                      <BoldText>{t.title}</BoldText>
                      <DimSubText>{t.entity_name}</DimSubText>
                    </td>
                    <RightTd>
                      <PeriodText>{formatMoney(t.accrued_amount)}</PeriodText>
                    </RightTd>
                    <ReservedPurpleTd>
                      {formatMoney(t.reserved_amount)}
                    </ReservedPurpleTd>
                    <PaidGreenTd>
                      {formatMoney(t.paid_amount)}
                    </PaidGreenTd>
                    <td>
                      <Badge $tone={conf.tone}>{conf.label}</Badge>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </TableWrap>
    </Section>
  );
}
